import { Suspense } from 'react';
import { Subscriptions } from './Subscriptions';
import { Alert } from '@startuptools/ui/Alert';
import { Card } from '@startuptools/ui/Card';
import { Link } from '@startuptools/ui/Link';
import { Stack } from '@startuptools/ui/Stack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { CompanySubscription } from './CompanySubscription';
import { PaymentMethods } from './PaymentMethods';
import { Invoices } from './Invoices';
import { useBillingSuspenseQuery } from './Billing.graphql';
import { useCompanySuspenseQuery, useCurrentUserSuspenseQuery } from '../../graphql/react-operations';
import { Trans } from '@lingui/react';
import { isNil } from 'lodash-es';
import { environment } from '../../../environments/environment';
import { Chip } from '@startuptools/ui/Chip';
import { Typography } from '@startuptools/ui/Typography';
import { useOnce } from '@startuptools/common/react-hooks';
import { useApolloClient } from '@apollo/client';
import { CompanySubscriptionDocument } from '../../components/side-nav/CompanySubscription.graphql';
import { apiClient } from '../../common/http';
import { captureException } from '@sentry/react';

const BillingInner = () => {
  const companyId = useCompanyId();
  const apollo = useApolloClient();
  const {
    data: { currentUser: user },
  } = useCurrentUserSuspenseQuery();

  const {
    data: { company },
  } = useCompanySuspenseQuery({ variables: { id: companyId } });
  const {
    data: { stripeProducts, stripeCustomer, stripeInvoices },
  } = useBillingSuspenseQuery({ variables: { companyId } });
  const stripeCompanySubscription = null;

  if (company.isDemo) {
    return (
      <Stack gap={2}>
        <Card>
          <Alert color="neutral">
            <Trans id="This is a demo company, no paid plan needed" />
          </Alert>
        </Card>
      </Stack>
    );
  }

  useOnce(() => {
    // Very hacky way of making sure we refresh CompanySubscription in SideNav in case we have new data from stripe
    setTimeout(() => {
      return apollo.refetchQueries({ include: [CompanySubscriptionDocument] });
    }, 0);
  });

  useOnce(() => {
    try {
      if (user.id) {
        void apiClient.setHubSpotUpgradePageVisit(user.id);
      }
    } catch (error) {
      captureException(error);
    }
  });

  const noDefaultPm =
    stripeCompanySubscription &&
    !isNil(stripeCustomer) &&
    isNil(stripeCustomer.invoice_settings.default_payment_method);

  return (
    <Stack gap={2}>
      {noDefaultPm ? (
        <Alert color="warning" variant="outlined" startDecorator={<WarningAmberIcon />}>
          <Trans id="In order to avoid cancellation of subscription, you must add a valid payment card and it must be set as default payment method" />
        </Alert>
      ) : (
        <Card>
          <Subscriptions stripeCompanySubscription={stripeCompanySubscription} />
        </Card>
      )}

      {user.admin && stripeCustomer && (
        <Card>
          <Typography component="span">
            <Trans
              id="<link>Open Stripe Customer</link> <chip>admin</chip>"
              components={{
                link: (
                  <Link
                    href={`https://dashboard.stripe.com/${environment.production ? '' : 'test/'}customers/${stripeCustomer.id}`}
                    target="_blank"
                  />
                ),
                chip: <Chip variant="outlined" size="sm" />,
              }}
            />
          </Typography>
        </Card>
      )}

      {stripeCompanySubscription && (
        <Card>
          <CompanySubscription stripeCompanySubscription={stripeCompanySubscription} stripeProducts={stripeProducts} />
        </Card>
      )}

      {stripeCustomer && (
        <Card>
          <PaymentMethods stripeCustomer={stripeCustomer} />
        </Card>
      )}

      {stripeInvoices.length > 0 && (
        <Card>
          <Invoices invoices={stripeInvoices} />
        </Card>
      )}
    </Stack>
  );
};

export const Billing = () => {
  return (
    <Suspense>
      <BillingInner />
    </Suspense>
  );
};
